import React, {
    useCallback, useEffect, useContext, useState
} from 'react';
import _ from 'lodash';
import { withRouter } from 'react-router-dom';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { ModalNextProvider } from '@jutro/components';
import { TranslatorContext } from '@jutro/locale';
import { useValidation } from 'gw-portals-validation-react';
import {
    WizardPage,
    wizardProps,
    WizardPageTemplate,
    WizardPageTemplateWithTitle
} from 'gw-portals-wizard-react';
import { ViewModelForm } from 'gw-portals-viewmodel-react';
import metadata from './WhatPage.metadata.json5';
import styles from './WhatPage.module.scss';
import { messages as commonMessages } from 'gw-platform-translations';
import { messages as cpCommonMessages } from 'gw-capability-fnol-cp-react/FNOLCP.messages';
import messages from '../../FNOLHO.messages';
import FNOLCriminalDamagePage from '../CriminalDamage/CriminalDamagePage';
import FNOLFireDamagePage from '../FireDamage/FireDamagePage';
import FNOLWaterDamagePage from '../WaterDamage/WaterDamagePage';
import FNOLWindDamagePage from '../WindDamage/WindDamagePage';
import FNOLOtherDamagePage from '../OtherDamage/OtherDamagePage';

function FNOLHOWhatPage(props) {
    const translator = useContext(TranslatorContext);
    const {
        wizardData: claimVM,
        updateWizardData,
        history,
        steps,
        changeNextSteps,
        authHeader,
        history: {
            location: { state = {} }
        }
    } = props;
    const {
        onValidate, isComponentValid, initialValidation, registerInitialComponentValidation
    } = useValidation('FNOLHOWhatPage');

    const { FNOLService } = useDependencies('FNOLService');
    const [pageData] = useState(state);
    const [liabilityAndPRTypeCodes, updateLiabilityAndPRTypeCodes] = useState([]);
    const [glTypeCodes, updateGLTypeCodes] = useState([]);
    const [prTypeCodes, updatePRTypeCodes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const onHandleClaimTypeChange = (value, path) => {
        _.set(claimVM, path, value);
        updateWizardData(claimVM);
    };


    const overrideProps = {
        '@field': {
            showOptional: false,
            showRequired: true
        },
        typeOfClaimDropDown: {
            availableValues: liabilityAndPRTypeCodes
        },
        glLossCategory: {
            visible: _.get(claimVM, 'lossType.value.code') === 'GL',
            availableValues: glTypeCodes
        },
        prLossCategory: {
            visible: _.get(claimVM, 'lossType.value.code') === 'PR',
            availableValues: prTypeCodes
        },
        dateOfNotice: {
            defaultValue: new Date()
        },

    };
    const resolvers = {
        resolveClassNameMap: styles,
        resolveCallbackMap: {
            onHandleClaimTypeChange: onHandleClaimTypeChange
        }
    };

    const { claimStatus } = state;

    const validateClaimStatus = useCallback(() => {
        return !_.isEmpty(claimStatus) && claimStatus === translator({ id: 'typekey.ClaimState.draft', defaultMessage: 'Draft' });
    }, [claimStatus, translator]);

    useEffect(() => {
        registerInitialComponentValidation(validateClaimStatus);
    }, [validateClaimStatus, registerInitialComponentValidation]);

    useEffect(() => {
        const detailsGLAndPRClaimTypeCodes = _.filter(
            claimVM.lossType.aspects.availableValues,
            (typeCode) => {
                const excludeClaimTypeCodesList = [
                    'AUTO',
                    'TRAV',
                    'WC'
                ];
                return excludeClaimTypeCodesList.indexOf(typeCode.code) === -1;
            }
        ).map((claimTypeObj) => {
            return {
                code: claimTypeObj.code,
                name: translator({
                    id: claimTypeObj.name,
                    defaultMessage: claimTypeObj.name
                })
            };
        });

        const detailsGLLoss = claimVM.gllossCause.aspects.availableValues.map((typeObj) => {
            return {
                code: typeObj.code,
                name: translator({
                    id: typeObj.name,
                    defaultMessage: typeObj.name
                })
            };
        });

        const detailsPRLoss = claimVM.prfarmLossCause.aspects.availableValues.map((typeObj) => {
            return {
                code: typeObj.code,
                name: translator({
                    id: typeObj.name,
                    defaultMessage: typeObj.name
                })
            };
        })
        updateLiabilityAndPRTypeCodes(detailsGLAndPRClaimTypeCodes);
        updateGLTypeCodes(detailsGLLoss);
        updatePRTypeCodes(detailsPRLoss);
    }, []);

    function getLossCategory(claimVM) {
        const lossCause = _.get(claimVM, 'lossCause.value.code');
        switch (lossCause) {
            case 'fire':
                return 'fire';
            case 'water':
            case 'mold':
                return 'water';
            case 'burglary':
            case 'vandalism':
            case 'riotandcivil':
                return 'burglary';
            case 'wind':
                return 'wind';
            case 'theftvandalism':
                return 'theftvandalism';
            case 'othercauses':
                return 'othercauses';
            default:
                return 'othercauses';
        }
    }

    const criminalDamage = {
        id: 'details',
        path: '/details',
        component: FNOLCriminalDamagePage,
        stepProps: {
            template: WizardPageTemplateWithTitle
        },
        title: messages.hoDetails
    };

    const waterDamage = {
        id: 'details',
        path: '/details',
        component: FNOLWaterDamagePage,
        stepProps: {
            template: WizardPageTemplateWithTitle
        },
        title: messages.hoDetails
    };

    const fireDamage = {
        id: 'details',
        path: '/details',
        component: FNOLFireDamagePage,
        stepProps: {
            template: WizardPageTemplateWithTitle
        },
        title: messages.hoDetails
    };

    const windDamage = {
        id: 'details',
        path: '/details',
        component: FNOLWindDamagePage,
        stepProps: {
            template: WizardPageTemplateWithTitle
        },
        title: messages.hoDetails
    };

    const otherDamage = {
        id: 'details',
        path: '/details',
        component: FNOLOtherDamagePage,
        stepProps: {
            template: WizardPageTemplateWithTitle
        },
        title: messages.hoDetails
    };

    const incidentPageHandling = useCallback(() => {
        const detailsPageIndex = 2;
        const changeArrayIndex = 0;
        let remainingSteps = steps.slice(detailsPageIndex, steps.length);
        if(_.get(claimVM, 'lossType.value.code') === 'PR'){

            /*if(_.indexOf(remainingSteps, {path: '/details'}) == -1){
                remainingSteps = [{}].concat(remainingSteps);
            } */

            switch (getLossCategory(claimVM)) {
                case 'fire':
                    remainingSteps[changeArrayIndex] = fireDamage;
                    break;
                // case 'mold':
                case 'water':
                    remainingSteps[changeArrayIndex] = waterDamage;
                    break;
                // case 'burglary':
                // case 'vandalism':
                // case 'riotandcivil':
                case 'theftvandalism':
                    remainingSteps[changeArrayIndex] = criminalDamage;
                    break;
                case 'wind':
                    remainingSteps[changeArrayIndex] = windDamage;
                    break;
                default:
                    remainingSteps[changeArrayIndex] = otherDamage;
            }
        }
        else if (remainingSteps[0].path === '/details') {
            remainingSteps = steps.slice((detailsPageIndex + 1), steps.length);
        }

        changeNextSteps(remainingSteps);
        return claimVM;
    }, [changeNextSteps, claimVM, steps]);

    const onNext = useCallback(() => {
        const policyNumber = _.get(claimVM, 'policy.policyNumber.value');
        const isClaimNumberAvailable = _.get(claimVM, 'claimNumber.value');
        if (isClaimNumberAvailable) {
            return incidentPageHandling();
        }
        setIsLoading(true);
        return FNOLService.getFNOLDetails(policyNumber, claimVM.value, authHeader)
            .then((response) => {
                claimVM.value = response;
                return incidentPageHandling();
            })
            .catch(() => {
                ModalNextProvider.showAlert({
                    title: messages.hoCreateDraftErrorTitle,
                    message: messages.hoCreateDraftErrorMessage,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok
                }).catch(_.noop);
                return false;
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [FNOLService, authHeader, claimVM, history, incidentPageHandling, pageData]);

    return (
        <WizardPage
            onNext={onNext}
            disableNext={!isComponentValid}
            skipWhen={initialValidation}
            template={WizardPageTemplate}
        >
            <ViewModelForm
                uiProps={metadata.pageContent}
                model={claimVM}
                overrideProps={overrideProps}
                onModelChange={updateWizardData}
                onValidationChange={onValidate}
                classNameMap={resolvers.resolveClassNameMap}
            />
        </WizardPage>
    );
}

FNOLHOWhatPage.propTypes = wizardProps;
export default withRouter(withAuthenticationContext(FNOLHOWhatPage));

